/* Shopify CAPTCHA bridge for the theme's AJAX customer logins. */
(() => {
  const pending = new WeakMap();
  const readyTimeout = 15000;
  const verificationTimeout = 180000;
  const requestTimeout = 30000;

  function loginError(code, message) {
    return Object.assign(new Error(message), { code });
  }

  function loading(name) {
    // Some entry points don't have the loading modal mounted.
    try { if (typeof window[name] === 'function') window[name](); } catch (_) { /* optional UI */ }
  }

  function previewParams() {
    const current = new URL(window.location.href);
    const params = new URLSearchParams();
    const theme = window.Shopify?.theme;
    const explicitId = current.searchParams.get('preview_theme_id');
    const id = explicitId || (['development', 'unpublished', 'demo'].includes(theme?.role) ? String(theme.id) : '');
    if (/^[1-9]\d*$/.test(id)) params.set('preview_theme_id', id);
    for (const key of ['_fd', 'pb']) {
      if (current.searchParams.has(key)) params.set(key, current.searchParams.get(key));
    }
    return params;
  }

  function accountUrl({ tab } = {}) {
    const params = previewParams();
    if (tab) params.set('tab', tab);
    const hostname = new URL(window.location.href).hostname;
    // Shopify CLI 3.x proxies a bare /account rather than rendering the local
    // template. A normal tab query keeps account pages in the theme renderer.
    if (['localhost', '127.0.0.1', '[::1]'].includes(hostname) && !params.toString()) {
      params.set('tab', 'home');
    }
    return `/account${params.toString() ? `?${params}` : ''}`;
  }

  function submit({ email, password, form: sourceForm }) {
    if (!email || !password) {
      return Promise.reject(loginError('LOGIN_INPUT', '이메일과 비밀번호를 입력해 주세요.'));
    }
    return protectedPost(new URLSearchParams({
      form_type: 'customer_login', utf8: '✓',
      'customer[email]': email, 'customer[password]': password,
      // Keep Shopify's authentication POST and its redirect canonical. Preview
      // context is reapplied by accountUrl() only after login succeeds.
      return_to: '/account'
    }), '/account/login', sourceForm || document.body, true);
  }

  function submitRegistration(sourceForm) {
    if (!sourceForm || sourceForm.elements.form_type?.value !== 'create_customer') {
      return Promise.reject(loginError('FORM_TYPE', '회원가입 양식을 확인하지 못했습니다.'));
    }
    return protectedPost(new FormData(sourceForm), '/account', sourceForm, false);
  }

  function protectedPost(fields, action, owner, isLogin) {
    if (pending.has(owner)) {
      return Promise.reject(loginError('LOGIN_BUSY', '요청을 처리 중입니다.'));
    }

    // A fresh form prevents reusing an expired token or stacking CAPTCHA wrappers
    // on a modal that can be opened repeatedly. Passwords stay only in memory/DOM.
    const form = document.createElement('form');
    form.action = action;
    form.method = 'post';
    form.style.margin = '0';
    for (const [name, value] of fields.entries()) {
      if (['h-captcha-response', 'g-recaptcha-response', 'recaptcha-v3-token', 'form_key'].includes(name)) continue;
      const input = document.createElement('input');
      input.type = name === 'customer[password]' ? 'password' : 'hidden';
      input.hidden = true;
      input.name = name;
      input.value = value;
      form.append(input);
    }

    let finished = false;
    let sent = false;
    let pollTimer;
    let deadlineTimer;
    let fetchTimer;
    let controller;
    let resolveLogin;
    let rejectLogin;
    const promise = new Promise((resolve, reject) => {
      resolveLogin = resolve;
      rejectLogin = reject;
    });
    pending.set(owner, promise);
    const previousBusy = owner.getAttribute('aria-busy');
    owner.setAttribute('aria-busy', 'true');

    function finish(error, response) {
      if (finished) return;
      finished = true;
      clearTimeout(pollTimer);
      clearTimeout(deadlineTimer);
      clearTimeout(fetchTimer);
      pending.delete(owner);
      if (previousBusy === null) owner.removeAttribute('aria-busy');
      else owner.setAttribute('aria-busy', previousBusy);
      if (form.elements['customer[password]']) form.elements['customer[password]'].value = '';
      form.remove();
      loading('closeLoadingModal');
      if (error) rejectLogin(error);
      else resolveLogin(response);
    }

    // protect() captures this submission function, then wraps it with Shopify's
    // verification. Its ready callback alone does NOT mean a token was issued.
    // The captured function is reached only after form.submit() acquires a token.
    const sendVerifiedForm = async () => {
      if (finished || sent) return;
      try {
        const body = new URLSearchParams(new FormData(form));
        if (!body.get('h-captcha-response')) {
          finish(loginError('CAPTCHA_TOKEN', '보안 인증을 완료하지 못했습니다. 다시 시도해 주세요.'));
          return;
        }
        sent = true;
        clearTimeout(deadlineTimer);
        controller = new AbortController();
        fetchTimer = setTimeout(() => {
          finish(loginError('LOGIN_TIMEOUT', '로그인 응답이 지연되고 있습니다. 다시 시도해 주세요.'));
          controller.abort();
        }, requestTimeout);
        loading('openLoadingModal');
        const response = await fetch(form.action, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString(), credentials: 'include', signal: controller.signal
        });
        if (finished) return;
        const url = new URL(response.url, window.location.href);
        if (/(?:^|\/)challenge(?:\/|$)/.test(url.pathname)) {
          // Legacy challenge pages must be opened, never treated as login success.
          finish(loginError('CAPTCHA_CHALLENGE', '추가 보안 인증이 필요합니다.'));
          window.location.assign(url.href);
          return;
        }
        if (!response.ok) {
          throw loginError('LOGIN_HTTP', '로그인 요청을 처리하지 못했습니다. 다시 시도해 주세요.');
        }
        if (isLogin && /(?:^|\/)account\/login\/?$/.test(url.pathname)) {
          throw loginError('LOGIN_FAILED', '이메일 또는 비밀번호를 확인해 주세요.');
        }
        if (url.origin !== window.location.origin || (isLogin && !/(?:^|\/)account\/?$/.test(url.pathname))) {
          throw loginError('LOGIN_RESPONSE', '로그인 결과를 확인하지 못했습니다. 다시 시도해 주세요.');
        }
        finish(null, response);
      } catch (error) {
        finish(error.code ? error : loginError('LOGIN_NETWORK', '로그인 중 오류가 발생했습니다. 다시 시도해 주세요.'));
      }
    };
    form.submit = sendVerifiedForm;
    document.body.append(form);
    // Keep the loading overlay out of the way of an interactive CAPTCHA.
    loading('closeLoadingModal');
    const readyDeadline = Date.now() + readyTimeout;
    deadlineTimer = setTimeout(() => {
      finish(loginError('CAPTCHA_TIMEOUT', '보안 인증 시간이 초과되었습니다. 다시 시도해 주세요.'));
    }, verificationTimeout);

    function protect() {
      if (finished) return;
      const captcha = window.Shopify?.captcha;
      if (typeof captcha?.protect !== 'function') {
        if (Date.now() >= readyDeadline) {
          finish(loginError('CAPTCHA_UNAVAILABLE', '보안 인증을 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.'));
        } else {
          pollTimer = setTimeout(protect, 100);
        }
        return;
      }
      try {
        captcha.protect(form, () => {
          if (finished) return;
          if (form.submit === sendVerifiedForm) {
            finish(loginError('CAPTCHA_UNAVAILABLE', '보안 인증을 준비하지 못했습니다. 다시 시도해 주세요.'));
            return;
          }
          form.submit();
        });
      } catch (error) {
        finish(loginError('CAPTCHA_UNAVAILABLE', '보안 인증을 준비하지 못했습니다. 다시 시도해 주세요.'));
      }
    }
    protect();
    return promise;
  }

  function showError(scope, error) {
    if (error?.code === 'LOGIN_BUSY' || error?.code === 'CAPTCHA_CHALLENGE') return;
    loading('closeLoadingModal');
    const text = error?.code ? error.message : '로그인 중 오류가 발생했습니다. 다시 시도해 주세요.';
    const container = scope?.isConnected ? scope : document.querySelector('.email_login__form');
    if (!container) {
      window.alert(text);
      return;
    }
    let message = container.querySelector('.msg_user_pw, [data-login-error]');
    if (!message) {
      message = document.createElement('div');
      message.className = 'err__msg';
      message.setAttribute('data-login-error', '');
      container.append(message);
    }
    message.setAttribute('role', 'alert');
    message.textContent = text;
    message.classList.remove('hidden');
  }

  // Intercept Enter/native submits before Shopify's source-form handler. The
  // bridge's fresh form performs the protected submission exactly once.
  function bindForm(form, button) {
    if (!form || !button || form.dataset.sidizLoginBound) return;
    form.dataset.sidizLoginBound = 'true';
    form.addEventListener('submit', event => {
      event.preventDefault();
      event.stopImmediatePropagation();
      button.click();
    }, true);
  }

  window.SidizCustomerLogin = { submit, submitRegistration, showError, bindForm, accountUrl };
})();
